import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { Check, ShieldCheck, Clock, Lock } from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

export default function Pricing({ onCta }) {
  const { t } = useLang();
  const f1 = [1, 2, 3, 4, 5, 6].map((n) => t(`pricing.p1.f${n}`));
  const f2 = [1, 2, 3, 4, 5, 6].map((n) => t(`pricing.p2.f${n}`));

  return (
    <section id="prezzi" className="py-20 lg:py-28" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <motion.div {...reveal} transition={{ duration: 0.7 }} className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00AEEF]">{t("pricing.kicker")}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#003087] mt-3">{t("pricing.title")}</h2>
          <p className="mt-4 text-base text-[#64748B]">{t("pricing.sub")}</p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-2 gap-8 items-stretch">
          <motion.div {...reveal} transition={{ duration: 0.7, delay: 0.1 }}
            className="card rounded-3xl p-8 flex flex-col" data-testid="pricing-box-visure">
            <span className="self-start text-[11px] font-bold uppercase tracking-[0.15em] bg-[#E6F6FD] text-[#003087] rounded-full px-4 py-1.5">{t("pricing.p1.tag")}</span>
            <h3 className="text-xl sm:text-2xl font-bold text-[#003087] mt-5">{t("pricing.p1.name")}</h3>
            <div className="mt-4 flex items-end gap-2">
              <span className="text-5xl font-extrabold text-[#00AEEF] tracking-tight">{t("pricing.p1.price")}</span>
              <span className="text-[#64748B] text-sm mb-2">{t("pricing.p1.per")}</span>
            </div>
            <ul className="mt-7 space-y-3 flex-1">
              {f1.map((f, i) => (
                <li key={i} className="flex gap-3 text-sm text-[#1A1A1A]">
                  <Check size={16} className="text-[#00AEEF] shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex items-center gap-2 text-xs text-[#00AEEF] font-semibold">
              <Clock size={14} /> 48-72h
            </div>
            <button data-testid="pricing-order-visure-button" onClick={() => onCta("visure")}
              className="btn-green mt-6 rounded-full px-6 py-4 text-sm font-bold">
              {t("pricing.p1.cta")}
            </button>
            <p className="mt-3 text-center text-[11px] text-[#94A3B8] flex items-center justify-center gap-1.5">
              <Lock size={11} /> {t("pricing.p1.pay")}
            </p>
          </motion.div>

          <motion.div {...reveal} transition={{ duration: 0.7, delay: 0.2 }}
            className="relative rounded-3xl p-8 flex flex-col bg-[#003087] text-white overflow-hidden shadow-2xl"
            data-testid="pricing-box-cancellazione">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#00AEEF]/20 blur-[90px] pointer-events-none" />
            <div className="flex items-center justify-between flex-wrap gap-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.15em] bg-white/10 text-white rounded-full px-4 py-1.5">{t("pricing.p2.tag")}</span>
              <span className="inline-flex items-center gap-1.5 bg-[#00AEEF] text-white text-[11px] font-bold uppercase tracking-wider rounded-full px-3 py-1.5">
                <ShieldCheck size={13} /> 0€ anticipo
              </span>
            </div>
            <h3 className="text-2xl font-bold mt-5">{t("pricing.p2.name")}</h3>
            <div className="mt-4 flex items-end gap-3 flex-wrap">
              <span className="text-6xl font-extrabold tracking-tight text-white">{t("pricing.p2.price")}</span>
              <span className="text-[#9BDDF7] text-xs font-bold mb-2.5 uppercase tracking-wide">{t("pricing.p2.per")}</span>
            </div>
            <p className="mt-2 text-sm text-[#00AEEF] font-semibold">{t("pricing.p2.rate")}</p>
            <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-4" data-testid="pricing-tiers">
              <p className="text-[11px] uppercase tracking-wider font-bold text-[#9BDDF7]">{t("pricing.p2.tiers")}</p>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="rounded-xl bg-white/10 px-3 py-2.5 text-center" data-testid={`pricing-tier-${n}`}>
                    <p className="text-lg font-extrabold text-white">{t(`pricing.p2.t${n}p`)}</p>
                    <p className="text-[10px] font-semibold text-[#9BDDF7] leading-tight mt-0.5">{t(`pricing.p2.t${n}`)}</p>
                  </div>
                ))}
              </div>
            </div>
            <ul className="mt-7 space-y-3 flex-1">
              {f2.map((f, i) => (
                <li key={i} className="flex gap-3 text-sm text-[#DCE7F5]">
                  <Check size={16} className="text-[#00AEEF] shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <button data-testid="pricing-order-cancellazione-button" onClick={() => onCta("cancellazione")}
              className="btn-green mt-8 rounded-full px-8 py-4 text-sm font-bold">
              {t("pricing.p2.cta")}
            </button>
            <p className="mt-3 text-center text-[11px] text-[#8FA8CC]">{t("pricing.p2.note")}</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
